import React, { useState } from "react"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import { PDFDocument, StandardFonts, rgb } from "pdf-lib"
import { CircularProgress, IconButton } from "@mui/material"
import styles from "../UserModal.module.scss"
import moment from "moment"

export default function PDFDownload({
  userName,
  acceptedDate,
  pdfUrl,
  onDownloadStart = () => {},
  onDownloadComplete = () => {},
  onError = () => {}
}) {
  const [isDownloading, setIsDownloading] = useState(false)
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

  const handleDownload = async () => {
    if (!userName?.trim()) {
      onError("User name is required")
      return
    }

    if (!pdfUrl) {
      onError("PDF URL is required")
      return
    }

    setIsDownloading(true)
    onDownloadStart()

    await delay(500)

    try {
      const existingPdfBytes = await fetch(pdfUrl).then((res) => res.arrayBuffer())

      const pdfDoc = await PDFDocument.load(existingPdfBytes)

      /* -------- Add new page -------- */
      const page = pdfDoc.addPage()
      const { width, height } = page.getSize()

      const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

      let dateStr, timeStr

      if (acceptedDate) {
        const parsed = moment(acceptedDate.replace("Accepted on ", ""), "MMM DD, YYYY [at] hh:mm A")
        dateStr = parsed.format("MMMM DD, YYYY")
        timeStr = parsed.format("hh:mm A")
      } else {
        const now = moment()
        dateStr = now.format("MMMM DD, YYYY")
        timeStr = now.format("hh:mm A")
      }

      page.drawText("Policy & Procedure Acceptance", {
        x: width / 2 - 200,
        y: height - 80,
        size: 18,
        font: boldFont,
        color: rgb(0, 0, 0)
      })

      page.drawLine({
        start: { x: 50, y: height - 100 },
        end: { x: width - 50, y: height - 100 },
        thickness: 1,
        color: rgb(0.6, 0.6, 0.6)
      })

      const startY = height - 160
      const gap = 30

      page.drawText("Accepted by:", {
        x: 50,
        y: startY,
        size: 12,
        font: boldFont
      })
      page.drawText(userName, {
        x: 180,
        y: startY,
        size: 12,
        font
      })

      page.drawText("Date:", {
        x: 50,
        y: startY - gap,
        size: 12,
        font: boldFont
      })
      page.drawText(dateStr, {
        x: 180,
        y: startY - gap,
        size: 12,
        font
      })

      page.drawText("Time:", {
        x: 50,
        y: startY - gap * 2,
        size: 12,
        font: boldFont
      })
      page.drawText(timeStr, {
        x: 180,
        y: startY - gap * 2,
        size: 12,
        font
      })

      page.drawText("This document confirms acceptance of the Policy & Procedure", {
        x: width / 2 - 220,
        y: 80,
        size: 10,
        font,
        color: rgb(0.4, 0.4, 0.4)
      })

      const pdfBytes = await pdfDoc.save()
      const blob = new Blob([pdfBytes], { type: "application/pdf" })

      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = `Policy_Procedure_${userName.replace(/\s+/g, "_")}.pdf`
      link.click()

      URL.revokeObjectURL(link.href)

      onDownloadComplete()
    } catch (err) {
      console.error(err)
      onError(err.message || "Failed to process PDF")
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <IconButton onClick={handleDownload} disabled={isDownloading} className={styles.downloadButton}>
      <ArrowDownwardIcon fontSize="small" />

      {isDownloading && (
        <CircularProgress size={32} thickness={4} className={styles.downloadIcon} />
      )}
    </IconButton>
  )
}
